<script>
// Components
import Layout from "@/router/layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config.json";
import UserProfile from "@/router/views/users/user-profile"

// Helpers
import {authComputed} from "@/state/helpers";
import DateUtils from '@/helpers/date';
import UserUtils from '@/helpers/users';
import Storage from "@/helpers/storage"

// Services
import ScoreService from "@/services/ScoreService";
import UsersDataService from "@/services/UsersDataService"

// External Components
import DatePicker  from "vue2-datepicker";
import 'vue2-datepicker/index.css';
import {VueTabs, VTab} from 'vue-nav-tabs'
import 'vue-nav-tabs/themes/vue-tabs.css';
import CoolLightBox from 'vue-cool-lightbox'
import 'vue-cool-lightbox/dist/vue-cool-lightbox.min.css'

// Lib
import isEmpty from "is-empty";

export default {
  name: "score",
  page: {
    localePicker: () => import(`vue2-datepicker/locale/${this.$i18n.locale}`),
    title: "Score QRI",
    meta: [
      {
        name: "description",
        content: appConfig.description
      },
    ],
  },
  components: {
    Layout,
    PageHeader,
    DatePicker,
    VueTabs,
    VTab,
    UserProfile,
    CoolLightBox,
  },
  methods: {
    ...UserUtils,
    ...DateUtils,

    handlePageChange(value) {
      this.page = value;
      if (this.scoreSearch === 'filters')
        this.loadList();
      else if (this.scoreSearch === 'search')
        this.processSearch(false);
    },

    handleDisplayChange() {
      this.loadList();
    },

    handleDateChange() {
      let now = new this.$moment();
      switch(this.filters.dates.selected) {
        case "always":
          this.filters.fromDate = new this.$moment('2021-01-01').format('YYYY-MM-DD');
          this.filters.toDate = now.format('YYYY-MM-DD');
          break;
        case "today":
          this.filters.fromDate = now.format('YYYY-MM-DD');
          this.filters.toDate = now.format('YYYY-MM-DD');
          break;
        case "yesterday":
          this.filters.fromDate = now.subtract(1, 'day').format('YYYY-MM-DD');
          this.filters.toDate = this.filters.fromDate;
          break;
        case "week":
          this.filters.toDate = now.format('YYYY-MM-DD');
          this.filters.fromDate = now.subtract(1, 'week').startOf('day').format('YYYY-MM-DD');
          break;
        case "curMonth":
          this.filters.toDate = now.format('YYYY-MM-DD');
          this.filters.fromDate = now.startOf('month').format('YYYY-MM-DD');
          break;
        case "last30Day":
          this.filters.toDate = now.format('YYYY-MM-DD');
          this.filters.fromDate = now.subtract(30, 'day').startOf('day').format('YYYY-MM-DD');
          break;
        case "lastMonth":
          this.filters.fromDate = now.subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
          this.filters.toDate = now.endOf('month').format('YYYY-MM-DD');
          break;
        case "last3Month":
          this.filters.toDate = now.format('YYYY-MM-DD');
          this.filters.fromDate = now.subtract(3, 'month').startOf('day').format('YYYY-MM-DD');
          break;
        case "curYear":
          this.filters.toDate = now.format('YYYY-MM-DD');
          this.filters.fromDate = now.startOf('year').format('YYYY-MM-DD');
          break;
        case "custom":
          this.filters.fromDate = null;
          this.filters.toDate = null;
          break;
      }
    },

    getRequestParams() {
      const params = {};

      params["page"] = this.page - 1;
      params["size"] = this.pageSizes.selected;

      if (this.filters.dates.selected === 'custom' && this.filters.dates.dateRange !== null) {
        this.filters.fromDate = this.$moment(this.filters.dates.dateRange[0]).format("YYYY-MM-DD 00:00:00");
        this.filters.toDate = this.$moment(this.filters.dates.dateRange[1]).format("YYYY-MM-DD 23:59:59");
      }
      else if (this.filters.fromDate === null)
        this.handleDateChange();

      if (this.filters.toDate !== null)
        params['toDate'] = this.filters.toDate;

      if (this.filters.fromDate !== null)
        params['fromDate'] = this.filters.fromDate;

      params['gender'] = this.filters.genders.selected;
      params['userGroup'] = this.filters.userGroups.selected;
      params['country'] = this.filters.countries.selected;

      if (this.$store.state.layout.timezone === 'utc') {
        params['tz'] = this.$moment.tz.guess();
      }

      return params;
    },

    loadStats() {
      const params = this.getRequestParams();
      ScoreService
          .getStats(params)
          .then(response => {
            const {total, historyGraph} = response.data;
            this.stats.men.reals = total.men.reals;
            this.stats.women.reals = total.women.reals;
            this.stats.men.users = total.men.users;
            this.stats.women.users = total.women.users;

            this.$refs.avgScoreChart.updateSeries([
              {
                name: "Avg QRI",
                type: 'line',
                data: historyGraph.avgScore
              },{
                name: "Avg QRI processed",
                type: 'line',
                data: historyGraph.avgHistoryScore
              }
              ]);

            this.$refs.countScoreChart.updateSeries([
              {
                name: "Count Processed",
                type: 'column',
                data: historyGraph.countScore
              }
            ]);

            this.$refs.scoreStepsChart.updateSeries([
              {
                name: "1000+",
                type: 'bar',
                data: historyGraph.count1000
              },
              {
                name: "2000+",
                type: 'bar',
                data: historyGraph.count2000
              },
              {
                name: "3000+",
                type: 'bar',
                data: historyGraph.count3000
              },
              {
                name: "4000+",
                type: 'bar',
                data: historyGraph.count4000
              },
              {
                name: "5000+",
                type: 'bar',
                data: historyGraph.count5000
              },
              {
                name: "6000+",
                type: 'bar',
                data: historyGraph.count6000
              },
              {
                name: "7000+",
                type: 'bar',
                data: historyGraph.count7000
              },
              {
                name: "8000+",
                type: 'bar',
                data: historyGraph.count8000
              },
              {
                name: "9000+",
                type: 'bar',
                data: historyGraph.count9000
              }
            ]);
          })
          .catch(error => {
            window.console.error(error);
          });
    },

    loadList() {
      this.isLoading = true;
      const params = this.getRequestParams();
      this.generatorSearch = 'filters';
      ScoreService
          .getList(params)
          .then(response => {
            const { items, totalItems } = response.data;
            this.scores = items;
            this.count = totalItems;
            this.getMediaThumb();
          })
          .catch(error => {
            window.console.error(error);
            this.$toast.error(this.$t("generator.errorGeneratorNoResult"));
          })
          .finally(() => {
            this.isLoading = false;
          });
    },

    resetData() {
      this.scores = [];
      this.stats = {
        men: {
          reals: '-',
          users: '-',
        },
        women: {
          reals: '-',
          users: '-',
        },
      };
    },

    processFilters() {
      this.resetData();
      this.loadList();
      this.loadStats();
    },

    processSearch(resetPage) {
      if (isEmpty(this.filters.searchType)) {
        this.$toast.error(this.$t("generator.error_searchType"));
        return;
      }

      if (isEmpty(this.filters.searchData)) {
        this.$toast.error(this.$t("generator.error_searchData"));
        return;
      }

      this.scoreSearch = 'search';
      if (resetPage) {
        this.page = 1;
      }

      this.isLoading = true;
      const params = {
        type: this.filters.searchType.selected,
        data: this.filters.searchData,
        page: this.page - 1,
        size: this.pageSizes.selected,
      };

      ScoreService
          .search(params)
          .then(response => {
            const { items, totalItems } = response.data;
            this.scores = items;
            this.count = totalItems;
            this.isLoading = false;
            this.getMediaThumb();
          })
          .catch(error => {
            window.console.error(error);
            this.$toast.error(this.$t("generator.errorGeneratorNoResult"));
            this.isLoading = false;
          });
    },

    getMediaThumb() {
      if (this.scores.length > 0)
        this.scores.forEach(row => {
          if (row.photo && row.photo.length > 0)
            Storage.getUserMedia(row.userId, row.photo, 'generator-user-'+row.scoreId);
        });
    },

    _isEmpty(val) {
      return isEmpty(val);
    },

    searchUser(userId) {
      this.filters.searchType.selected = 'userId';
      this.filters.searchData = userId;
      this.processSearch(true);
    },

    showContextMenu(event, user) {
      this.$refs.generatorContextMenu.showMenu(event, user)
    },

    processContextMenu(event) {
      const { item, option } = event;
      switch (option.value) {
        case "show":
          this.showUser(item.userId);
          break;

        case "search":
          this.searchUser(item.userId);
          break;
      }
    },

    /* User profile */
    openUserPane() {
      if (!this.isUserPaneDisplayed)
        this.isUserPaneDisplayed = true;
    },

    closeUserPane() {
      this.userTabsActive = '';
      this.isUserPaneDisplayed = false;
    },

    async showUser(userId) {
      const isUserAlreadyDisplayed = this.userTabs.findIndex(x => x.userId === userId);
      let forceShow = false;
      if (isUserAlreadyDisplayed === -1) {
        try {
          const result = await UsersDataService.get(userId);
          if (typeof result.data === 'undefined')
            throw new Error('unable to find user '+userId);

          this.userTabs.push(UserUtils.normalize(result.data));
          forceShow = true;
        }
        catch (error) {
          window.console.error(error);
          this.$toast.error(this.$t("global_error"));
        }
      }

      this.openUserPane();

      if (forceShow || (!forceShow && this.userTabsActive !== userId)) {
        this.userTabsActive = '';
        setTimeout(() => { this.userTabsActive = userId; }, 100);
      }
    },

    showUserMedia(media, index) {
      this.userMedia.media = media;
      this.userMedia.index = index;
    },

    removeUserTab(userId) {
      const index = this.userTabs.findIndex(x => x.userId === userId);
      if (index > -1)
        this.userTabs.splice(index, 1);

      if (this.userTabs.length === 0)
        this.closeUserPane();
    }
  },
  mounted() {
    this.processFilters();
  },
  computed: {
    ...authComputed
  },
  data() {
    return {
      isLoading: false,
      isLoadingPerformance: false,
      title: this.$t("menuitems.dashboards.score"),
      scoreSearch: 'filters',
      scores: [],
      page: 1,
      count: 0,
      pageSizes: {
        options: ['20', '50', '100'],
        selected: '50'
      },
      optionsContext: [
        {
          name: this.$t("show_user"),
          value: 'show'
        },
        {
          name: this.$t("search_user"),
          value: 'search'
        },
      ],
      stats: {
        men: {
          reals: '-',
          users: '-',
        },
        women: {
          reals: '-',
          users: '-',
        },
      },
      countScoreGraph: {
        series: [],
        chartOptions: {
          chart: {
            type: 'bar',
            stacked: false,
            toolbar: {
              show: true,
              offsetX: 0,
              offsetY: 0,
              tools: {
                download: true,
                selection: false,
                zoom: false,
                zoomin: false,
                zoomout: false,
                pan: false,
                reset: false
              },
              export: {
                png: {
                  filename: 'score-count'
                },
                svg: {
                  filename: 'score-count'
                }
              }
            }
          },
          dataLabels: {
            enabled: false
          },
          xaxis: {
            type: 'datetime',
            labels: {
              show: true,
              style: {
                fontSize: 11
              },
              hideOverlappingLabels: true
            }
          },
          colors: ["#FF5722"],
          yaxis: [
            {
              axisTicks: {
                show: true,
              },
              axisBorder: {
                show: true,
              },
              title: {
                text: "Number",
              },
              tooltip: {
                enabled: true
              }
            }],
          tooltip: {
            shared: true,
            intersect: false
          },
          legend: {
            position: 'bottom',
          }
        }
      },
      avgScoreGraph: {
        series: [],
        chartOptions: {
          chart: {
            type: 'line',
            stacked: false,
            toolbar: {
              show: true,
              offsetX: 0,
              offsetY: 0,
              tools: {
                download: true,
                selection: false,
                zoom: false,
                zoomin: false,
                zoomout: false,
                pan: false,
                reset: false
              },
              export: {
                png: {
                  filename: 'avg-score'
                },
                svg: {
                  filename: 'avg-score'
                }
              }
            }
          },
          dataLabels: {
            enabled: false
          },
          stroke: {
            width: [1, 4]
          },
          colors: ["#556ee6", "#FF5722"],
          xaxis: {
            type: 'datetime',
            labels: {
              show: true,
              style: {
                fontSize: 11
              },
              hideOverlappingLabels: true
            }
          },
          yaxis: [
            {
              axisTicks: {
                show: true,
              },
              axisBorder: {
                show: true,
              },
              title: {
                text: "QRI Avg",
              },
              tooltip: {
                enabled: true
              }
            }
          ],
          tooltip: {
            shared: true,
            intersect: false
          },
          legend: {
            position: 'bottom',
          }
        },
      },
      scoreStepsGraph: {
        series: [],
        chartOptions: {
          chart: {
            type: 'bar',
            stacked: true,
            stackType: '100%',
            toolbar: {
              show: true,
              offsetX: 0,
              offsetY: 0,
              tools: {
                download: true,
                selection: false,
                zoom: false,
                zoomin: false,
                zoomout: false,
                pan: false,
                reset: false
              },
              export: {
                png: {
                  filename: 'score-steps'
                },
                svg: {
                  filename: 'score-steps'
                }
              }
            }
          },
          dataLabels: {
            enabled: false
          },
          xaxis: {
            type: 'datetime',
            labels: {
              show: true,
              style: {
                fontSize: 11
              },
              hideOverlappingLabels: true
            }
          },
          colors: ["#E91E63", "#FF5722", "#FFA726", "#FFEE58", "#AED581", "#29B6F6", "#5C6BC0", "#AB47BC", "#90A4AE"],
          yaxis: [
            {
              axisTicks: {
                show: true,
              },
              axisBorder: {
                show: true,
              },
              labels: {
              },
              title: {
                text: "Ratio",
              },
              tooltip: {
                enabled: true
              }
            }],
          tooltip: {
            shared: true,
            intersect: false
          },
          legend: {
            position: 'bottom',
          }
        }
      },
      filters: {
        searchType: {
          selected: 'userId',
          options: [
            {value:'userId', text:this.$t("userId")},
            {value:'email', text:this.$t("email")}
          ]
        },
        searchData: "",
        fromDate: null,
        toDate: null,
        dates: {
          dateRange: null,
          selected: 'week',
          options: [
            {value:'today', text:this.$t("dates.today")},
            {value:'yesterday', text:this.$t("dates.yesterday")},
            {value:'week', text:this.$t("dates.week")},
            {value:'curMonth', text:this.$t("dates.curMonth")},
            {value:'last30Day', text:this.$t("dates.last30Day")},
            {value:'lastMonth', text:this.$t("dates.lastMonth")},
            {value:'last3Month', text:this.$t("dates.last3Month")},
            {value:'curYear', text:this.$t("dates.curYear")},
            {value:'always', text:this.$t("dates.always")},
            {value:'custom', text:this.$t("dates.custom")}
          ]
        },
        genders: {
          selected: 'all',
          options: [
            {value: 'all', text: this.$t("users.all_genders")},
            {value: 1, text: this.$t("users.search.genderMale")},
            {value: 2, text: this.$t("users.search.genderFemale")}
          ]
        },
        userGroups: {
          selected: 'all',
          options: [
            {value: 'all', text: 'All User Groups'},
            {value: 'reals', text: 'Only Reals'},
            {value: 'users', text: 'Only Users'}
          ]
        },
        countries: {
          selected: 'all',
          options: [
            {value: 'all', text: 'All Countries'},
            {value: '', text: '----------------'},
            {value: 'FR', text: 'FR - France'},
            {value: 'BE', text: 'BE - Belgium'},
            {value: 'CH', text: 'CH - Switzerland'},
            {value: 'CA', text: 'CA - Canada'},
            {value: 'LU', text: 'LU - Luxembourg'},
            {value: '', text: '----------------'},
            {value: 'XX', text: 'Others'},
          ]
        },
      },
      /* User profile */
      isUserPaneDisplayed: false,
      userTabs:[],
      userTabsActive: '',
      userMedia: {
        media: [],
        index: -1
      },
    }
  }
}
</script>

<template>
  <Layout>
    <PageHeader :title="title" />
    <div class="position-relative">
      <b-button
          class="position-fixed"
          style="top: 77px; right: 15px;z-index:150"
          v-show="!isUserPaneDisplayed && userTabs.length > 0"
          @click="openUserPane"
          variant="outline-primary">
        <i class="bx bxs-cog"></i> Show Panel
      </b-button>

      <!-- Filters -->
      <div class="card">
        <div class="card-body">
          <!-- Search -->
          <b-form inline class="row gy-2 gx-3 align-items-center" id="scoreSearchForm" @submit.stop.prevent>
            <div class="col-sm-auto">
              <b-form-select
                  id="scoreSearchTypeFilter"
                  v-model="filters.searchType.selected"
                  class="form-select me-lg-1"
                  :options="filters.searchType.options">
              </b-form-select>
            </div>
            <div class="col-sm-auto">
              <b-form-input
                  id="scoreSearchData"
                  class="me-lg-1"
                  v-model="filters.searchData"
                  placeholder="Search…">
              </b-form-input>
            </div>
            <div class="col-sm-auto">
              <b-button variant="primary" class="w-md" @click="processSearch(true)">{{ $t("searchBtn") }}</b-button>
            </div>
          </b-form>
          <hr />
          <!-- filters -->
          <b-form inline class="row gy-2 gx-3 align-items-center" id="scoreFiltersForm" @submit.stop.prevent>

            <!-- dates -->
            <div class="col-sm-auto d-flex">
              <b-form-select
                  id="scoreDatesFilter"
                  @change="handleDateChange"
                  v-model="filters.dates.selected"
                  class="form-select me-lg-1"
                  :options="filters.dates.options">
              </b-form-select>
              <date-picker
                  v-show="filters.dates.selected === 'custom'"
                  :lang="page.localePicker"
                  :placeholder="this.$t('choose_date_interval')"
                  type="date"
                  value-type="format"
                  show-week-number
                  v-model="filters.dates.dateRange"
                  class="me-lg-2"
                  range append-to-body ></date-picker>

            </div>

            <!-- Genders -->
            <div class="col-sm-auto">
              <b-form-select
                  id="scoreGenderFilter"
                  v-model="filters.genders.selected"
                  class="form-select me-lg-1"
              >
                <option v-for="ct in filters.genders.options" :key="ct.value" :value="ct.value">{{ ct.text }}</option>
              </b-form-select>
            </div>

            <!-- Types -->
            <div class="col-sm-auto">
              <b-form-select
                  id="scoreTypeFilter"
                  v-model="filters.userGroups.selected"
                  class="form-select me-lg-1"
              >
                <option v-for="ct in filters.userGroups.options" :key="ct.value" :value="ct.value">{{ ct.text }}</option>
              </b-form-select>
            </div>

            <!-- Countries -->
            <div class="col-sm-auto">
              <b-form-select
                  id="leadsCountriesFilter"
                  v-model="filters.countries.selected"
                  class="form-select me-lg-1"
                  :options="filters.countries.options">
              </b-form-select>
            </div>

            <div class="col-sm-auto">
              <b-button variant="primary" class="w-md" @click="processFilters">{{ $t("find") }}</b-button>
            </div>
          </b-form>
        </div>
      </div>

      <!-- Loader -->
      <div class="row d-flex justify-content-center">
        <vue-ellipse-progress
            v-show="isLoading"
            :loading="isLoading"
            :angle="-90"
            color="#50a5f1"
            emptyColor="#999"
            emptyColorFill="#f8f8fb"
            :size="100"
            :thickness="3"
            :emptyThickness="2"
            lineMode="out 2"
            animation="bounce 700 400"
            fontColor="#666"
            :half="false"
            class="mt-5 mb-5"
            fontSize="1rem">
        </vue-ellipse-progress>
      </div>

      <!-- Stats Plain -->
      <div class="row" v-if="scoreSearch === 'filters'">
        <!-- men reals -->
        <div class="col-lg-3">
          <div class="card mini-stats-wid">
            <div class="card-body">
              <div class="d-flex flex-wrap">
                <div class="me-3">
                  <p class="text-muted mb-2">Men QRI Avg</p>
                  <h5 class="mb-0">{{ stats.men.reals }}</h5>
                </div>
                <div class="avatar-sm ms-auto">
                  <div
                      class="avatar-title bg-info rounded-circle white font-size-20"
                  >
                    <i class="bx bxs-user"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- women reals -->
        <div class="col-lg-3">
          <div class="card mini-stats-wid">
            <div class="card-body">
              <div class="d-flex flex-wrap">
                <div class="me-3">
                  <p class="text-muted mb-2">Women QRI Avg</p>
                  <h5 class="mb-0">{{ stats.women.reals }}</h5>
                </div>
                <div class="avatar-sm ms-auto">
                  <div
                      class="avatar-title bg-pink rounded-circle white font-size-20"
                  >
                    <i class="bx bxs-user"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- men Specials -->
        <div class="col-lg-3">
          <div class="card mini-stats-wid">
            <div class="card-body">
              <div class="d-flex flex-wrap">
                <div class="me-3">
                  <p class="text-muted mb-2">Men (Specials) QRI Avg</p>
                  <h5 class="mb-0">{{ stats.men.users }}</h5>
                </div>
                <div class="avatar-sm ms-auto">
                  <div
                      class="avatar-title bg-info rounded-circle white font-size-20"
                  >
                    <i class="bx bxs-user"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- women reals -->
        <div class="col-lg-3">
          <div class="card mini-stats-wid">
            <div class="card-body">
              <div class="d-flex flex-wrap">
                <div class="me-3">
                  <p class="text-muted mb-2">Women (Specials) QRI Avg</p>
                  <h5 class="mb-0">{{ stats.women.users }}</h5>
                </div>
                <div class="avatar-sm ms-auto">
                  <div
                      class="avatar-title bg-pink rounded-circle white font-size-20"
                  >
                    <i class="bx bxs-user"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>

      <!-- Graph -->
      <div class="row" v-if="scoreSearch === 'filters'">

        <div class="col-12">

          <!-- Step Score Graph -->
          <div class="card">
            <apexchart
                ref="scoreStepsChart"
                class="apex-charts"
                type="bar"
                dir="ltr"
                height="300"
                :series="scoreStepsGraph.series"
                :options="scoreStepsGraph.chartOptions"
            ></apexchart>
          </div>
          <br>
          <!-- Avg Score Graph -->
          <div class="card">
            <apexchart
                ref="avgScoreChart"
                class="apex-charts"
                type="line"
                dir="ltr"
                height="300"
                :series="avgScoreGraph.series"
                :options="avgScoreGraph.chartOptions"
            ></apexchart>
          </div>
          <br>
          <!-- Count Score Graph -->
          <div class="card">
            <apexchart
                ref="countScoreChart"
                class="apex-charts"
                type="bar"
                dir="ltr"
                height="200"
                :series="countScoreGraph.series"
                :options="countScoreGraph.chartOptions"
            ></apexchart>
          </div>

        </div>

      </div>

      <!-- Top list tools -->
      <div class="row mb-3">
        <!-- Display settings -->
        <div class="col-lg-auto col-12">
          <b-select class="form-select d-inline w-auto m-1" v-model="pageSizes.selected" @change="handleDisplayChange">
            <option v-for="size in pageSizes.options" :key="size" :value="size">{{ size }} {{ $t('pageSize') }}</option>
          </b-select>

          {{ count }} {{ $t('resultsCount') }}
        </div>
        <!-- Pagination -->
        <div class="col-lg-auto col-12">
          <div class="m-1 pagination-rounded">
            <b-pagination
                v-model="page"
                :total-rows="count"
                :per-page="pageSizes.selected"
                first-number
                last-number
                @change="handlePageChange"
                aria-controls="generator-list"
            ></b-pagination>
          </div>
        </div>
      </div>

      <!-- Result list -->
      <div class="row bg-white" v-if="!isLoading && scores.length > 0">
        <div class="table-responsive card-body">
          <table
              id="generator-list"
              class="table project-list-table table-nowrap table-centered table-borderless align-middle"
          >
            <thead>
            <tr>
              <th scope="col">{{ $t('score.indexedAt') }}</th>
              <th scope="col">{{ $t('score.user') }}</th>
              <th scope="col">{{ $t('score.userGroup') }}</th>
              <th scope="col">{{ $t('score.qrIndex') }}</th>
              <th scope="col">{{ $t('action') }}</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="row in scores" :key="row.scoreId" @contextmenu.prevent.stop="showContextMenu($event, row)">
              <td>
                <p class="text-muted font-size-12">{{ formatDateString(row.indexedDateAt, 'DD MMM YYYY') }}</p>
              </td>
              <td>
                <div class="media">
                  <div class="avatar-xs">
                    <img
                        :id="`generator-user-${row.scoreId}`"
                        :src="require('@/assets/images/users/default-user.png')"
                        class="d-flex rounded-circle avatar-xs" />
                  </div>
                  <div class="media-body ms-1">
                    <h5 class="font-size-14 mb-0">
                      <span
                          :class="{
                          'text-primary': row.sex === 1,
                          'text-danger': row.sex === 2,
                          'text-dark': row.sex === 0,
                        }"
                      >
                        {{ row.name || '-' }}
                      </span> {{ getAge(row.birth) }}</h5>
                    <small class="text-muted">#{{ row.userId }}</small>
                  </div>
                </div>
              </td>
              <td>
                <span
                    class="badge"
                    :class="{
                        'bg-success text-light': `${row.userGroup}` === 'real',
                        'bg-light text-dark': `${row.userGroup}` === 'user'
                      }"
                >{{ $t(`score.group_${row.userGroup}`) }}</span>
              </td>
              <td>
                <p class="fs-4"><strong>{{ row.qrIndex }}</strong></p>
              </td>
              <td>
                <b-dropdown
                    class="card-drop"
                    variant="white"
                    menu-class="dropdown-menu-end"
                    right
                    toggle-class="p-0"
                >
                  <template v-slot:button-content>
                    <i class="mdi mdi-dots-horizontal font-size-18"></i>
                  </template>
                  <b-dropdown-item
                      href="javascript: void(0);"
                      @click="showUser(row.userId)"
                  >{{ $t("show_user") }}</b-dropdown-item>
                  <b-dropdown-item
                      href="javascript: void(0);"
                      @click="searchUser(row.userId)"
                  >{{ $t("search_user")}}</b-dropdown-item>
                </b-dropdown>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Result Empty -->
      <div class="row" v-if="!isLoading && scores.length === 0">
        <div class="col-12">
          <div class="card">
            <div class="card-body">
              <h4 class="card-title">Empty Result</h4>
              <p>No Score QRI were found for these filters or search. Try again.</p>
            </div>
          </div>
        </div>
      </div>

      <!-- bottom list tools -->
      <div class="row mb-3">
        <!-- Display settings -->
        <div class="col-lg-auto col-12">
          <b-select class="form-select d-inline w-auto m-1" v-model="pageSizes.selected" @change="handleDisplayChange">
            <option v-for="size in pageSizes.options" :key="size" :value="size">{{ size }} {{ $t('pageSize') }}</option>
          </b-select>
        </div>
        <!-- Pagination -->
        <div class="col-lg-auto col-12">
          <div class="m-1 pagination-rounded">
            <b-pagination
                v-model="page"
                :total-rows="count"
                :per-page="pageSizes.selected"
                first-number
                last-number
                @change="handlePageChange"
                aria-controls="generator-list"
            ></b-pagination>
          </div>
        </div>
      </div>

    </div>
    <b-sidebar id="sidebar-right" right v-model="isUserPaneDisplayed">
      <vue-tabs
          v-model="userTabsActive"
          active-tab-color="#213213"
          active-text-color="white">
        <v-tab v-for="userTab in userTabs" :key="userTab.userId" :title="userTab.userId" >
          <div slot="title">{{ userTab.name }} <i @click.stop="removeUserTab(userTab.userId)" class="bx bx-x-circle" ></i></div>
          <UserProfile
              :userId="null"
              :user="userTab"
              v-on:SHOW_MEDIA="showUserMedia">
          </UserProfile>
        </v-tab>
      </vue-tabs>
    </b-sidebar>

    <vue-simple-context-menu
        element-id="generatorContextMenu"
        :options="optionsContext"
        ref="generatorContextMenu"
        @option-clicked="processContextMenu"
    />

    <CoolLightBox
        :items="userMedia.media"
        :index="userMedia.index"
        :effect="'fade'"
        @close="userMedia.index = null">
    </CoolLightBox>
  </Layout>
</template>

<style>

#subscriptionsHistoryCountChart .btn input[type=radio] {
  position: absolute;
  clip: rect(0,0,0,0);
  pointer-events: none;
}

#subscriptionsHistoryCountChart .btn {
  margin-top: -8px;
  margin-bottom: 0 !important;
}

.bg-canceled {
  background-color:#f1b44c !important;
}

.bg-ended {
  background-color: #efa2c5 !important;
}

.b-sidebar-right {
  width:35%;
}

.b-sidebar-body {
  padding:16px;
}

</style>
